import { useCallback, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router';
import { Eye, MoreVertical, Pause, Play, Search, Square, Terminal, Trash2, X } from 'lucide-react';
import type { AdminServer, AdminSpace } from '@spawnwire/shared';
import { Button, clsx, DropdownMenu, FullscreenDialog, IconButton, Input, Spinner, type MenuEntry } from '../../components/ui';
import { errorMessage, http } from '../../lib/api';
import { getSocket, onReady } from '../../lib/gateway';
import { formatDate } from '../../lib/moderation';
import { useStore } from '../../store/store';
import { ui } from '../../store/ui';
import { FileManager } from '../minecraft/FileManager';
import { STATE_LABEL, STATE_TONE, isTransitional } from '../minecraft/MinecraftPanels';
import { minecraft, useMinecraft } from '../minecraft/store';
import { SpaceIcon } from '../shell/SpaceIcon';
import { Empty, PageHeader, ReasonDialog, UserChip } from './adminUi';

function SearchBox({ value, onChange, placeholder }: { value: string; onChange: (v: string) => void; placeholder: string }) {
  return (
    <div className="relative mb-4">
      <Search className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-faint" aria-hidden />
      <Input value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} className="pl-9" aria-label={placeholder} />
    </div>
  );
}

/** A live, read-only console for staff checking a server. */
function StaffConsole({ serverId }: { serverId: string }) {
  const lines = useMinecraft((s) => s.console[serverId]);
  const [error, setError] = useState<string | null>(null);
  const box = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const subscribe = () =>
      getSocket()?.emit('mc:console:subscribe', { serverId }, (res) => {
        if (res.ok) minecraft.setConsole(serverId, res.data?.lines ?? []);
        else setError(res.error);
      });
    subscribe();
    const off = onReady(subscribe);
    return () => {
      off();
      getSocket()?.emit('mc:console:unsubscribe', { serverId });
    };
  }, [serverId]);

  useEffect(() => {
    if (box.current) box.current.scrollTop = box.current.scrollHeight;
  }, [lines]);

  return (
    <div ref={box} className="scroll-thin h-80 overflow-y-auto rounded-xl border border-line bg-overlay px-4 py-3 font-mono text-xs leading-5 text-fg [overflow-wrap:anywhere]" role="log">
      {error ? <p className="text-danger">{error}</p> : !lines ? <Spinner className="size-4" /> : lines.length === 0 ? <p className="text-muted">No output yet.</p> : lines.map((l, i) => <div key={i} className="whitespace-pre-wrap">{l}</div>)}
    </div>
  );
}

function Inspect({ entry, onClose }: { entry: AdminServer; onClose: () => void }) {
  const s = entry.server;
  return (
    <FullscreenDialog open onClose={onClose} title={`Inspect ${s.name}`}>
      <div className="flex min-w-0 flex-1 flex-col">
        <header className="flex h-12 shrink-0 items-center gap-3 border-b border-line px-3">
          <IconButton icon={X} label="Close" onClick={onClose} />
          <span className="min-w-0 flex-1 truncate text-sm font-semibold text-fg">
            {s.name} <span className="font-normal text-muted">· {s.address} · owned by @{entry.owner.username}</span>
          </span>
          <span className="text-xs text-muted">Read only</span>
        </header>
        <div className="scroll-thin flex-1 space-y-4 overflow-y-auto p-4 sm:p-6">
          <section>
            <h2 className="mb-2 flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-muted">
              <Terminal className="size-4" aria-hidden /> Console
            </h2>
            {s.state === 'suspended' ? <p className="text-sm text-muted">The console is closed while the server is suspended.</p> : <StaffConsole serverId={s.id} />}
          </section>
          <FileManager server={s} uploadMaxBytes={0} base={`/admin/servers/${s.id}/files`} readOnly />
        </div>
      </div>
    </FullscreenDialog>
  );
}

export function ServersPage() {
  const isAdmin = useStore((s) => s.me?.staffRole === 'admin');
  const [params, setParams] = useSearchParams();
  const q = params.get('q') ?? '';
  const [rows, setRows] = useState<AdminServer[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [inspecting, setInspecting] = useState<AdminServer | null>(null);
  const [dialog, setDialog] = useState<{ kind: 'suspend' | 'delete'; entry: AdminServer } | null>(null);

  const load = useCallback(async () => {
    try {
      setRows(await http.get<AdminServer[]>(`/admin/servers?q=${encodeURIComponent(q)}`));
      setError(null);
    } catch (err) {
      setError(errorMessage(err));
    }
  }, [q]);

  useEffect(() => {
    const t = setTimeout(() => void load(), 250);
    return () => clearTimeout(t);
  }, [load]);

  const replace = (entry: AdminServer) => setRows((all) => (all ?? []).map((r) => (r.server.id === entry.server.id ? entry : r)));

  async function run(path: string, done: string) {
    try {
      replace(await http.post<AdminServer>(path));
      ui.toast(done, 'success');
    } catch (err) {
      ui.toast(errorMessage(err), 'error');
    }
  }

  function entries(entry: AdminServer): MenuEntry[] {
    const s = entry.server;
    const suspended = !!entry.suspendedAt;
    return [
      { label: 'Inspect console and files', icon: Eye, onSelect: () => setInspecting(entry) },
      { label: 'Stop', icon: Square, hidden: suspended || s.state === 'offline' || s.state === 'installing', onSelect: () => void run(`/admin/servers/${s.id}/stop`, `Stopping ${s.name}.`) },
      { label: 'Suspend', icon: Pause, hidden: suspended, onSelect: () => setDialog({ kind: 'suspend', entry }) },
      { label: 'Lift suspension', icon: Play, hidden: !suspended, onSelect: () => void run(`/admin/servers/${s.id}/unsuspend`, `${s.name} is no longer suspended.`) },
      { label: 'Delete server', icon: Trash2, danger: true, separatorBefore: true, hidden: !isAdmin, onSelect: () => setDialog({ kind: 'delete', entry }) },
    ];
  }

  return (
    <div>
      <PageHeader title="Game servers" description="Every server hosted on Spawnwire. Suspending stops it and locks it on the panel until you lift it." />
      <SearchBox value={q} onChange={(v) => setParams(v ? { q: v } : {}, { replace: true })} placeholder="Search by server name, address or owner" />
      {error && <p className="mb-3 text-sm text-danger">{error}</p>}
      {!rows ? (
        <Spinner />
      ) : rows.length === 0 ? (
        <Empty>No servers.</Empty>
      ) : (
        <ul className="space-y-2">
          {rows.map((entry) => {
            const s = entry.server;
            return (
              <li key={s.id} className={clsx('flex flex-wrap items-center gap-4 rounded-xl border bg-sidebar p-4', entry.suspendedAt ? 'border-danger/50' : 'border-line')}>
                <div className="min-w-0 flex-1">
                  <p className="flex items-center gap-2 font-semibold text-fg">
                    <span className="truncate">{s.name}</span>
                    {entry.suspendedAt && <span className="rounded bg-danger px-1.5 py-0.5 text-[11px] font-bold uppercase text-white">Suspended</span>}
                  </p>
                  <p className="truncate text-xs text-muted">
                    {s.software === 'paper' ? 'Paper' : 'Vanilla'} {s.version} · <span className="font-mono">{s.address}</span> · created {formatDate(s.createdAt)}
                  </p>
                  {entry.suspendedAt && (
                    <p className="mt-1 text-xs text-danger">
                      Suspended {formatDate(entry.suspendedAt)} by @{entry.suspendedBy?.username ?? 'unknown'}: {s.suspendReason}
                    </p>
                  )}
                </div>
                <UserChip user={entry.owner} />
                <span className="flex w-28 items-center gap-1.5 text-sm text-muted">
                  <span className={clsx('size-2 rounded-full', STATE_TONE[s.state], isTransitional(s.state) && 'animate-pulse')} aria-hidden />
                  {STATE_LABEL[s.state]}
                  {s.players && <span className="text-xs">· {s.players.online}</span>}
                </span>
                <DropdownMenu entries={entries(entry)} trigger={<IconButton icon={MoreVertical} label={`Actions for ${s.name}`} />} />
              </li>
            );
          })}
        </ul>
      )}
      {inspecting && <Inspect entry={inspecting} onClose={() => setInspecting(null)} />}
      {dialog?.kind === 'suspend' && (
        <ReasonDialog
          title={`Suspend ${dialog.entry.server.name}?`}
          description="It stops at once. The owner sees your reason and can't start it, use its console or touch its files until you lift the suspension."
          confirmLabel="Suspend server"
          danger
          placeholder="e.g. Running a crypto miner."
          onClose={() => setDialog(null)}
          onSubmit={async (reason) => {
            replace(await http.post<AdminServer>(`/admin/servers/${dialog.entry.server.id}/suspend`, { reason }));
            ui.toast('Server suspended.', 'success');
          }}
        />
      )}
      {dialog?.kind === 'delete' && (
        <ReasonDialog
          title={`Delete ${dialog.entry.server.name}?`}
          description="This permanently deletes the server and its world from the panel. It cannot be undone."
          confirmLabel="Delete server"
          danger
          onClose={() => setDialog(null)}
          onSubmit={async (reason) => {
            await http.del(`/admin/servers/${dialog.entry.server.id}`, { reason });
            setRows((all) => (all ?? []).filter((r) => r.server.id !== dialog.entry.server.id));
            ui.toast('Server deleted.', 'success');
          }}
        />
      )}
    </div>
  );
}

export function SpacesPage() {
  const isAdmin = useStore((s) => s.me?.staffRole === 'admin');
  const [q, setQ] = useState('');
  const [rows, setRows] = useState<AdminSpace[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [removing, setRemoving] = useState<AdminSpace | null>(null);

  const load = useCallback(async () => {
    try {
      setRows(await http.get<AdminSpace[]>(`/admin/spaces?q=${encodeURIComponent(q)}`));
      setError(null);
    } catch (err) {
      setError(errorMessage(err));
    }
  }, [q]);

  useEffect(() => {
    const t = setTimeout(() => void load(), 250);
    return () => clearTimeout(t);
  }, [load]);

  return (
    <div>
      <PageHeader title="Spaces" description="Every community on Spawnwire, newest first." />
      <SearchBox value={q} onChange={setQ} placeholder="Search spaces by name or ID" />
      {error && <p className="mb-3 text-sm text-danger">{error}</p>}
      {!rows ? (
        <Spinner />
      ) : rows.length === 0 ? (
        <Empty>No spaces.</Empty>
      ) : (
        <ul className="space-y-2">
          {rows.map((s) => (
            <li key={s.id} className="flex flex-wrap items-center gap-4 rounded-xl border border-line bg-sidebar p-4">
              <SpaceIcon name={s.name} iconUrl={s.iconUrl} size={40} />
              <div className="min-w-0 flex-1">
                <p className="truncate font-semibold text-fg">{s.name}</p>
                <p className="text-xs text-muted">
                  {s.memberCount} member{s.memberCount === 1 ? '' : 's'} · created {formatDate(s.createdAt)}
                </p>
              </div>
              <UserChip user={s.owner} />
              {isAdmin && (
                <Button size="sm" variant="ghost" icon={Trash2} className="text-danger hover:text-danger" onClick={() => setRemoving(s)}>
                  Remove
                </Button>
              )}
            </li>
          ))}
        </ul>
      )}
      {removing && (
        <ReasonDialog
          title={`Remove ${removing.name}?`}
          description={`This permanently deletes the space, its channels and messages for all ${removing.memberCount} members. It cannot be undone.`}
          confirmLabel="Remove space"
          danger
          onClose={() => setRemoving(null)}
          onSubmit={async (reason) => {
            await http.del(`/admin/spaces/${removing.id}`, { reason });
            setRows((all) => (all ?? []).filter((x) => x.id !== removing.id));
            ui.toast('Space removed.', 'success');
          }}
        />
      )}
    </div>
  );
}
